import { isVisible } from '@/core/utils/dom';
import { isMacOS } from '@/core/utils/device';
import type { DirectiveBinding } from 'vue';

export type ShortcutValue = string;

/**
 * Допустимые модификаторы
 */
type AllowedModifiers = 'ctrl' | 'shift' | 'alt';
type Binding = DirectiveBinding<ShortcutValue, AllowedModifiers>;

/**
 * Список горячих клавиш и html элементов, на которые они назначены
 */
const elementByShortcut = new Map<string, HTMLElement>();

/**
 * Добавлен ли обработчик события
 */
let isListenerAttached = false;

/**
 * Отображение названий клавиш-модификаторов в зависимости от ОС
 *
 * В наших горячих клавишах Ctrl всегда соответствует Cmd в MacOS
 */
const modifierKeys = {
	ctrl: isMacOS() ? '⌘' : 'Ctrl',
	alt: isMacOS() ? '⌥' : 'Alt',
	shift: isMacOS() ? '⇧' : 'Shift',
};

/**
 * Получить название клавиши из event.code
 */
const getKeyLabel = (code: string) => {
	let keyLabel = code;

	if (code.includes('Key')) keyLabel = code.replace('Key', '');
	if (code.includes('Digit')) keyLabel = code.replace('Digit', '');
	if (code === 'Escape') keyLabel = 'Esc';

	return keyLabel;
};

/**
 * Сгенерировать строковое представление сочетания клавиш с модификаторами
 *
 * Приводим к виду "Ctrl+Shift+A" или "⌘⇧A" для MacOS
 */
const genShortcutString = (code: string, modifiers: Binding['modifiers']) => {
	const keyLabel = getKeyLabel(code);
	if (!modifiers) return keyLabel;

	const { ctrl, alt, shift } = modifierKeys;
	const parts: string[] = [];

	if (modifiers.ctrl) parts.push(ctrl);
	if (modifiers.alt) parts.push(alt);
	if (modifiers.shift) parts.push(shift);

	// Основная клавиша
	parts.push(keyLabel);

	return parts.join(isMacOS() ? '' : '+');
};

const registerShortcut = (el: HTMLElement, value: ShortcutValue, modifiers: Binding['modifiers']) => {
	const shortcut = genShortcutString(value, modifiers);

	if (el['__TOP_V_SHORTCUT_VALUE']) {
		console.warn(`Element already has shortcut "${el['__TOP_V_SHORTCUT_VALUE']}"`);

		return;
	}

	if (elementByShortcut.has(shortcut)) {
		console.warn(`Shortcut "${shortcut}" is taken`);

		return;
	}

	elementByShortcut.set(shortcut, el);
	el.title = el.title ? `${el.title} (${shortcut})` : shortcut;
	el['__TOP_V_SHORTCUT_VALUE'] = shortcut;
};

const unregisterShortcut = (el: HTMLElement) => {
	const shortcut: string | undefined = el['__TOP_V_SHORTCUT_VALUE'];
	if (!shortcut) return;

	elementByShortcut.delete(shortcut);
};

const attachGlobalListener = () => {
	if (isListenerAttached) return;
	isListenerAttached = true;

	document.addEventListener('keydown', e => {
		if (!(e.target instanceof HTMLElement)) return;
		if (['INPUT', 'TEXTAREA'].includes(e.target.tagName) || e.target.isContentEditable) return;

		const modifiers = {
			ctrl: isMacOS() ? e.metaKey : e.ctrlKey,
			alt: e.altKey,
			shift: e.shiftKey,
		};

		const shortcut = genShortcutString(e.code, modifiers);
		const element = elementByShortcut.get(shortcut);

		if (!element) return;

		if (!(element as any).disabled && isVisible(element)) {
			e.preventDefault();
			element.click();
		}
	});
};

const shortcut = {
	mounted: function (el: HTMLElement, binding: Binding) {
		const { value, modifiers } = binding;
		if (!value) return;

		attachGlobalListener();
		registerShortcut(el, value, modifiers);
	},

	unmounted(el: HTMLElement) {
		unregisterShortcut(el);
	},
};

export default shortcut;
